import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/realtime-store/redux-store";
import PaginationParameters from "common/contracts/pagination-parameters";
import Guid from "common/values/guid/guid";
import _ from "lodash";
import MessagingAPIService from "messaging/api/messaging-api-service";
import QueryForumsApiRequest, {
  ForumFilterParameters,
  ForumOrderParameters
} from "messaging/entities/forum/api/request-contracts/query-forums-api-request";
import Forum from "messaging/entities/forum/forum";
import Topic from "messaging/values/topic";
import { useSelector } from "react-redux";
import Session from "users/session/session";

type ForumStoreState = {
  byId: {
    entries: Record<string, Forum>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  },
  byTopic: {
    entries: Record<string, Forum[]>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  },
  byQuery: {
    entries: Record<string, Forum[]>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  },
  interfaceQueries: {
    entries: Record<string, QueryForumsApiRequest>;
  };
};

const initialState: ForumStoreState = {
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  byTopic: {
    entries: {},
    loading: {},
    error: {},
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {}
  }
};

export const populateForums = createAsyncThunk<
  Forum[],
  { session: Session; forumIds: Guid[] }
>(
  "forums/getForumsByIds",
  async ({ session, forumIds }: { session: Session; forumIds: Guid[] }, thunkAPI) => {
    if (!forumIds.length) return [];

    const store: { forums: ForumStoreState } = thunkAPI.getState() as { forums: ForumStoreState };
    const missingForumsIds = _.uniqWith(
      forumIds.filter((forumId) => !store.forums.byId.entries[forumId.value]),
      (a, b) => a.isEqualTo(b)
    );
    if (!missingForumsIds.length) return [];

    const apiService = new MessagingAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getForumsByIds(forumIds, abortController);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const populateForumsByTopic = createAsyncThunk(
  "forums/getForumsByTopic",
  async ({ session, topic }: { session: Session, topic: Topic }, thunkAPI) => {
    const apiService = new MessagingAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      const filter = new ForumFilterParameters();
      filter.context = topic.context;
      filter.entityClass = topic.entityClass;
      filter.entityId = topic.entityId?.value;
      const order = new ForumOrderParameters();
      const pagination = new PaginationParameters(0, 100);
      const request = new QueryForumsApiRequest(
        filter,
        order,
        pagination
      );
      return await apiService.getForums(request, abortController);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const populateForumsByQuery = createAsyncThunk<
  Forum[],
  { session: Session, query: QueryForumsApiRequest }
>(
  "forums/getForumsByQuery",
  async (
    { session, query }: { session: Session, query: QueryForumsApiRequest },
    thunkAPI
  ) => {
    const apiService = new MessagingAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getForums(query, abortController);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

const forumsSlice = createSlice({
  name: "forums",
  initialState,
  reducers: {
    addForum: (state, action: PayloadAction<Forum>) => {
      if (action.payload.id) {
        state.byId.entries[action.payload.id.value] = action.payload;
      }
      if (action.payload.topic) {
        const topicKey = action.payload.topic.toString();
        if (!state.byTopic.entries[topicKey]) {
          state.byTopic.entries[topicKey] = [];
        }
        state.byTopic.entries[topicKey].push(action.payload);
      }
    },
    updateForumInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: QueryForumsApiRequest;
      }>
    ) => {
      state.interfaceQueries.entries[action.payload.interfaceName] =
        action.payload.query;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      populateForumsByTopic.pending,
      (state, action) => {
        const topic = action.meta.arg.topic.toString();
        if (!topic) return;
        state.byTopic.loading[topic] = true;
        state.byTopic.error[topic] = null;
      }
    );
    builder.addCase(
      populateForumsByTopic.fulfilled,
      (state, action) => {
        const topic = action.meta.arg.topic.toString();
        if (!topic) return;

        state.byTopic.loading[topic] = false;
        state.byTopic.entries[topic] = action.payload;

        for (const forum of action.payload) {
          if (forum.id) {
            state.byId.entries[forum.id.value] = forum;
          }
        }
      }
    );
    builder.addCase(
      populateForumsByTopic.rejected,
      (state, action) => {
        const topic = action.meta.arg.topic.toString();
        if (!topic) return;
        state.byTopic.loading[topic] = false;
        state.byTopic.error[topic] = action.error.message || "Failed to load forums for topic";
      }
    );

    builder.addCase(
      populateForums.pending,
      (state, action) => {
        const forumIds = action.meta.arg.forumIds;
        if (!forumIds.length) return;

        for (const id of forumIds) {
          state.byId.loading[id.value] = true;
          state.byId.error[id.value] = null;
        }
      }
    );
    builder.addCase(
      populateForums.fulfilled,
      (state, action) => {
        const forumIds = action.meta.arg.forumIds;
        if (!forumIds.length) return;

        for (const forum of action.payload) {
          state.byId.entries[forum.id?.value as string] = forum;
        }

        for (const id of forumIds) {
          state.byId.loading[id.value] = false;
          if (!state.byId.entries[id.value]) {
            state.byId.error[id.value] = "Forum not found";
          }
        }
      }
    );
    builder.addCase(
      populateForums.rejected,
      (state, action) => {
        const forumIds = action.meta.arg.forumIds;
        if (!forumIds.length) return;

        for (const id of forumIds) {
          state.byId.loading[id.value] = false;
          state.byId.error[id.value] = action.error.message || "Error fetching forum info.";
        }
      }
    );

    builder.addCase(
      populateForumsByQuery.pending,
      (state, action) => {
        const query = action.meta.arg.query.asSearchParams().toString();
        state.byQuery.loading[query] = true;
        state.byQuery.error[query] = null;
      }
    );
    builder.addCase(
      populateForumsByQuery.fulfilled,
      (state, action) => {
        const query = action.meta.arg.query.asSearchParams().toString();
        state.byQuery.entries[query] = action.payload;
        state.byQuery.loading[query] = false;
        state.byQuery.error[query] = null;

        for (const forum of action.payload) {
          if (!forum.id) continue;
          state.byId.entries[forum.id.value] = forum;
          state.byId.loading[forum.id.value] = false;
          state.byId.error[forum.id.value] = null;
        }
      }
    );
    builder.addCase(
      populateForumsByQuery.rejected,
      (state, action) => {
        const query = action.meta.arg.query.asSearchParams().toString();
        state.byQuery.loading[query] = false;
        state.byQuery.error[query] = action.payload as string;
      }
    );
  }
});

export const { addForum, updateForumInterfaceQuery } = forumsSlice.actions;

export const getForumsByTopic = (topic: Topic) =>
  useSelector(
    (state: RootState) => state.forums.byTopic.entries[topic.toString()]
  );
export const getIsLoadingForumsByTopic = (topic: Topic) =>
  useSelector(
    (state: RootState) => state.forums.byTopic.loading[topic.toString()]
  );
export const getErrorLoadingForumsByTopic = (topic: Topic) =>
  useSelector(
    (state: RootState) => state.forums.byTopic.error[topic.toString()]
  );

export const getForumsByIds = (ids: Guid[]) =>
  useSelector(
    createSelector(
      (state: RootState) => state.forums.byId.entries,
      (entries) => ids.map(id => entries[id.toString()])
    )
  );
export const getLoadingForumIds = () => {
  const state = useSelector((state: RootState) => state.forums.byId.loading);
  return Object.keys(state).filter((forumId) => state[forumId]).map((forumId) => Guid.fromJSON(forumId));
}
export const getAreErrorsLoadingForumsByIds = (ids: Guid[]) =>
  useSelector(
    (state: RootState) => ids.some(id => state.forums.byId.error[id.toString()] !== null)
  );

export const getForumsByQuery = (query: QueryForumsApiRequest) => {
  return useSelector((state: RootState) => {
    if (!query) {
      return undefined;
    }
    return state.forums.byQuery.entries[
      query.asSearchParams().toString()
    ];
  });
};
export const getForumQueryByInterface = (interfaceName: string) =>
  useSelector(
    (state: RootState) =>
      state.forums.interfaceQueries.entries[interfaceName]
  );
export const getIsLoadingForumsByQuery = (query: QueryForumsApiRequest) =>
  useSelector((state: RootState) => {
    if (!query) return false;
    return state.forums.byQuery.loading[
      query.asSearchParams().toString()
    ] ?? false;
  });
export const getErrorLoadingForumsByQuery = (query: QueryForumsApiRequest) =>
  useSelector((state: RootState) => {
    if (!query) return false;
    return state.forums.byQuery.error[query.asSearchParams().toString()] ?? false;
  });

export type ForumError = {
  id: Guid;
  error: string;
};
export const getAllForumErrors = (): ForumError[] => {
  const state = useSelector((state: RootState) => state.forums.byId.error);
  return Object.keys(state)
    .filter((forumId) => forumId && state[forumId])
    .map((forumId) => {
      const id = Guid.fromJSON(forumId);
      const error = state[forumId];
      if (!id || !error) return null;
      return { id, error } as ForumError;
    })
    .filter((item): item is ForumError => item !== null);
};

export default forumsSlice;
